import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';
import styled from '@emotion/styled';
import React from 'react';

import { css } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import usePlatform from '~/shared/hooks/platform';
import { skin } from '~/theme';

const appStoreCodes = {
  en: 'us',
  ko: 'kr',
  ja: 'jp',
  'zh-Hant': 'tw',
  'zh-Hans': 'cn',
  th: 'th',
} as const;

interface DownloadButtonsProps {
  className?: string;
  type: 'small' | 'large';
}

const Container = styled('div')(
  skin({
    blackSquare: css({
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
    }),
  }),
);

const StoreLink = styled('a')(
  skin({
    blackSquare: css({
      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
      borderRadius: 80,
      display: 'inline-block',
      height: '100%',
    }),
  }),
);

const StoreBanner = styled('img')(
  skin({
    blackSquare: css({
      width: '100%',
      height: 'auto',
    }),
  }),
);

const DownloadButtons: React.FC<DownloadButtonsProps> = ({ className, type }) => {
  const locale = useLocale();
  const { googleAppId, appleAppId, onestoreAppId, onelink, galaxystoreUrl } = useFrontmatter();

  const platform = usePlatform();
  const isIos = platform === 'ios';
  const isAndroid = platform === 'android';
  const isMaybeDesktop = !isAndroid && !isIos;

  const appStoreTarget = appStoreCodes[locale as keyof typeof appStoreCodes] || 'CA';

  return (
    <Container className={className}>
      {appleAppId && (isMaybeDesktop || isIos) && (
        <StoreLink
          target='_blank'
          rel='noopener'
          href={isMaybeDesktop ? `https://apps.apple.com/${appStoreTarget}/app/id${appleAppId}` : onelink}
          className={'appstore-button'}
        >
          <StoreBanner
            src={
              require(
                `!@svgr/webpack!file-loader!~/assets/images/appstore${isMaybeDesktop && type !== 'small' ? '' : '-mobile'}.svg`,
              ).default
            }
          />
        </StoreLink>
      )}
      {googleAppId && (isMaybeDesktop || isAndroid) && (
        <>
          <StoreLink
            target='_blank'
            rel='noopener'
            href={isMaybeDesktop ? `https://play.google.com/store/apps/details?id=${googleAppId}` : onelink}
            className={'playstore-button'}
          >
            <StoreBanner
              src={
                require(
                  `!@svgr/webpack!file-loader!~/assets/images/playstore${isMaybeDesktop && type !== 'small' ? '' : '-mobile'}.svg`,
                ).default
              }
            />
          </StoreLink>
          {onestoreAppId && (
            <StoreLink
              target='_blank'
              rel='noopener'
              href={`https://onesto.re/${onestoreAppId}`}
              className={'onestore-button'}
            >
              <StoreBanner
                src={
                  require(
                    `!@svgr/webpack!file-loader!~/assets/images/onestore${isMaybeDesktop && type !== 'small' ? '' : '-mobile'}.svg`,
                  ).default
                }
              />
            </StoreLink>
          )}
          {galaxystoreUrl && (
            <StoreLink target='_blank' rel='noopener' href={galaxystoreUrl}>
              <StoreBanner
                src={
                  require(
                    `!@svgr/webpack!file-loader!~/assets/images/galaxystore${isMaybeDesktop && type !== 'small' ? '' : '-mobile'}.svg`,
                  ).default
                }
              />
            </StoreLink>
          )}
        </>
      )}
    </Container>
  );
};

export default DownloadButtons;
